import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Form,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";

import { ActivityButton, SequenceNumberInput } from "../../../components/Common";
import { ParsedError } from "../../../interfaces";
import { CreateEducationalStageRequest } from "../../../interfaces/curriculum";
import { ErrorHandlingTypeEnum, SequenceEnum } from "../../../enums";
import { applyServerFormErrors } from "../../../services";
import {
  useAddEducationalStageMutation,
  useGetEducationalStageQuery,
  useUpdateEducationalStageMutation,
} from "../../../store/api/educationalStageApi";
import { useGetCurriculumSelectListQuery } from "../../../store/api/curriculumApi";
import { useGetCourseSelectListQuery } from "../../../store/api/courseApi";
import { useGetEducationSystemSelectListQuery } from "../../../store/api/educationSystemApi";

type TProps = {
  id?: string | null;
  defaultEducationSystemId?: string;
  defaultCurriculumId?: string;
  defaultCourseId?: string;
  show: boolean;
  onCloseClick: () => void;
};

export const AddEditEducationalStageModal: React.FC<TProps> = ({
  id,
  defaultEducationSystemId,
  defaultCurriculumId,
  defaultCourseId,
  show,
  onCloseClick,
}) => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const [educationSystemId, setEducationSystemId] = useState(
    defaultEducationSystemId || "",
  );

  const { data: educationSystems } = useGetEducationSystemSelectListQuery(
    undefined,
    { skip: !show },
  );
  const { data: curricula } = useGetCurriculumSelectListQuery(
    educationSystemId || undefined,
    { skip: !show || !educationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery(undefined, {
    skip: !show,
  });
  const { data: item, error: getError } = useGetEducationalStageQuery(
    id as string,
    { skip: !id || !show },
  );
  const [
    addItem,
    { isLoading: isAdding, isError: isErrorAdd, error: errorAdd },
  ] = useAddEducationalStageMutation();
  const [
    updateItem,
    { isLoading: isUpdating, isError: isErrorUpdate, error: errorUpdate },
  ] = useUpdateEducationalStageMutation();

  const getEmptyValues = (): CreateEducationalStageRequest => ({
    name: { en: "", ar: "" },
    code: "",
    curriculumId: defaultCurriculumId || "",
    courseId: defaultCourseId || "",
    sequenceNumber: 0,
  });

  const form = useFormik<CreateEducationalStageRequest>({
    enableReinitialize: false,
    initialValues: getEmptyValues(),
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(t("isNotEmpty", { displayName: "englishValue" })),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "arabicValue" }),
          )
          .required(t("isNotEmpty", { displayName: "arabicValue" })),
      }),
      code: Yup.string().required(t("isNotEmpty", { displayName: "code" })),
      curriculumId: Yup.string().required(
        t("isNotEmpty", { displayName: "curriculumItem" }),
      ),
      courseId: Yup.string().required(
        t("isNotEmpty", { displayName: "course" }),
      ),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", { displayName: "sequenceNumber" }),
      ),
    }),
    onSubmit: async (values) => {
      if (!educationSystemId || !values.curriculumId) {
        return;
      }
      try {
        const payload = {
          ...values,
          sequenceNumber: Number(values.sequenceNumber),
        };
        if (id) {
          await updateItem({ id, data: payload }).unwrap();
        } else {
          await addItem(payload).unwrap();
        }
        onClose();
      } catch {
        // Form field errors (e.g. duplicate code) are applied in the effect below.
      }
    },
  });

  const onClose = () => {
    form.resetForm({ values: getEmptyValues() });
    setEducationSystemId(defaultEducationSystemId || "");
    onCloseClick();
  };

  useEffect(() => {
    if (!show) {
      form.resetForm({ values: getEmptyValues() });
      setEducationSystemId(defaultEducationSystemId || "");
      return;
    }

    if (id && item) {
      setEducationSystemId(
        item.educationSystemId || defaultEducationSystemId || "",
      );
      form.setValues({
        name: item.name,
        code: item.code,
        curriculumId: item.curriculumId,
        courseId: item.courseId,
        sequenceNumber: item.sequenceNumber ?? 0,
      });
      return;
    }

    if (!id) {
      setEducationSystemId(defaultEducationSystemId || "");
      form.resetForm({ values: getEmptyValues() });
    }
  }, [
    show,
    id,
    item,
    defaultEducationSystemId,
    defaultCurriculumId,
    defaultCourseId,
  ]);

  // Keep curriculum valid for selected education system
  useEffect(() => {
    if (!show || !educationSystemId || !curricula) return;
    if (
      form.values.curriculumId &&
      curricula.some((c: any) => c.id === form.values.curriculumId)
    ) {
      return;
    }
    const preferred =
      (defaultCurriculumId &&
        curricula.find((c: any) => c.id === defaultCurriculumId)?.id) ||
      curricula[0]?.id ||
      "";
    if (form.values.curriculumId !== preferred) {
      form.setFieldValue("curriculumId", preferred);
    }
  }, [show, educationSystemId, curricula, defaultCurriculumId]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isAdding && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isUpdating && isErrorUpdate) _error = errorUpdate as ParsedError;

    if (_error?.type === ErrorHandlingTypeEnum.FORM && _error.error) {
      applyServerFormErrors(form, _error.error);
    }
  }, [isAdding, isUpdating, isErrorAdd, isErrorUpdate]);

  return (
    <Modal id="addEditEducationalStage" isOpen={show} toggle={onClose} centered>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
      >
        <ModalHeader className="p-3" toggle={onClose}>
          {id
            ? t("editItem", { item: "educationalStage" })
            : t("addItem", { item: "educationalStage" })}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">
              {t("educationSystem")}
              <span className="text-danger">*</span>
            </label>
            <Input
              type="select"
              value={educationSystemId}
              onChange={(e) => {
                setEducationSystemId(e.target.value);
                form.setFieldValue("curriculumId", "");
              }}
              invalid={!educationSystemId && form.submitCount > 0}
            >
              <option value="">{t("selectEducationSystem")}</option>
              {(educationSystems || []).map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.value?.[langKey] || s.value?.en}
                </option>
              ))}
            </Input>
            {!educationSystemId && form.submitCount > 0 ? (
              <FormFeedback type="invalid" className="d-block">
                {t("isNotEmpty", { displayName: "educationSystem" })}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("curriculumItem")}
              <span className="text-danger">*</span>
            </label>
            <Input
              type="select"
              name="curriculumId"
              value={form.values.curriculumId}
              disabled={!educationSystemId}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                !!(form.touched.curriculumId && form.errors.curriculumId)
              }
            >
              <option value="">{t("selectCurriculum")}</option>
              {(curricula || []).map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.value?.[langKey] || c.value?.en}
                </option>
              ))}
            </Input>
            {form.touched.curriculumId && form.errors.curriculumId ? (
              <FormFeedback type="invalid">
                {form.errors.curriculumId as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("course")}
              <span className="text-danger">*</span>
            </label>
            <Input
              type="select"
              name="courseId"
              value={form.values.courseId}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.courseId && form.errors.courseId)}
            >
              <option value="">{t("selectCourse")}</option>
              {(courses || []).map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.value?.[langKey] || c.value?.en}
                </option>
              ))}
            </Input>
            {form.touched.courseId && form.errors.courseId ? (
              <FormFeedback type="invalid">
                {form.errors.courseId as string}
              </FormFeedback>
            ) : null}
          </div>
          <SequenceNumberInput
            tabIndex={1}
            value={form.values.sequenceNumber}
            onChange={(e) => {
              e.preventDefault();
              const { value } = e.target;
              const regex =
                /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
              if (regex.test(value.toString())) {
                form.setFieldValue("sequenceNumber", value);
              } else {
                form.setFieldValue("sequenceNumber", "");
              }
            }}
            onBlur={form.handleBlur}
            invalid={
              !!(form.touched.sequenceNumber && form.errors.sequenceNumber)
            }
            sequenceType={SequenceEnum.EDUCATIONAL_STAGE}
            onGenerateSequenceNumber={(v: number) => {
              form.setFieldValue("sequenceNumber", v);
            }}
            mode={id ? "edit" : "add"}
            request={{
              type: SequenceEnum.EDUCATIONAL_STAGE,
              curriculumId: form.values.curriculumId || undefined,
              courseId: form.values.courseId || undefined,
            }}
          />
          {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
            <FormFeedback type="invalid" className="d-block">
              {form.errors.sequenceNumber as string}
            </FormFeedback>
          ) : null}
          <div className="mb-3">
            <label className="form-label">
              {t("code")}
              <span className="text-danger">*</span>
            </label>
            <Input
              name="code"
              value={form.values.code}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.code && form.errors.code)}
            />
            {form.touched.code && form.errors.code ? (
              <FormFeedback type="invalid">
                {form.errors.code as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("nameEn")}
              <span className="text-danger">*</span>
            </label>
            <Input
              name="name.en"
              value={form.values.name.en}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.name?.en && form.errors.name?.en)}
            />
            {form.touched.name?.en && form.errors.name?.en ? (
              <FormFeedback type="invalid">
                {form.errors.name?.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("nameAr")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              name="name.ar"
              value={form.values.name.ar}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.name?.ar && form.errors.name?.ar)}
            />
            {form.touched.name?.ar && form.errors.name?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.name?.ar as string}
              </FormFeedback>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link to={"#"} onClick={onClose} className="btn btn-link link-success">
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            type="submit"
            inProgress={isAdding || isUpdating}
            disabled={
              isAdding ||
              isUpdating ||
              !!getError ||
              !educationSystemId ||
              !form.values.curriculumId
            }
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};
